import React from 'react';
import type { ChatMessage } from '../../types/api';

export interface TimelineSection {
  id: string;
  timestamp: string;
  userPrompt?: string;
  prompt?: string; // Support both property names
  agentResponses?: Array<{
    id: string;
    content: string;
    model: string;
    toolUse?: Array<{
      name: string;
      description: string;
    }>;
    artifacts?: Array<{
      name: string;
      type: string;
    }>;
  }>;
  messages?: ChatMessage[]; // Support messages array from EnhancedChatPanel
  responseCount?: number;
  status: 'success' | 'error' | 'pending';
  hasCode?: boolean;
  hasArtifacts?: boolean;
}

export type FilterType = 'all' | 'code' | 'errors' | 'artifacts';

/**
 * Filter timeline sections by search query and category filter
 */
export function filterSections(
  sections: TimelineSection[],
  searchQuery: string,
  activeFilter: FilterType
): TimelineSection[] {
  let result = sections;

  // Apply search filter
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    result = result.filter(section => {
      const promptText = (section.userPrompt || section.prompt || '').toLowerCase();
      const responseText = section.agentResponses?.map(r => r.content).join(' ').toLowerCase() || '';
      return promptText.includes(query) || responseText.includes(query);
    });
  }

  // Apply category filter
  if (activeFilter !== 'all') {
    result = result.filter(section => {
      switch (activeFilter) {
        case 'code': return section.hasCode;
        case 'errors': return section.status === 'error';
        case 'artifacts': return section.hasArtifacts;
        default: return true;
      }
    });
  }

  return result;
}

/**
 * Highlight matching text by wrapping matches in <mark> elements
 * Returns original string if no query or no matches
 */
export function highlightText(
  text: string,
  searchQuery: string
): string | React.ReactNode[] {
  if (!searchQuery.trim()) return text;

  const query = searchQuery.trim();
  // Escape special regex characters to prevent regex injection
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedQuery})`, 'gi');
  const parts = text.split(regex);

  if (parts.length === 1) return text;

  return parts.map((part, i) =>
    regex.test(part)
      ? React.createElement('mark', {
          key: i,
          className: 'bg-warning-500/40 text-text-primary rounded px-0.5'
        }, part)
      : part
  );
}
